import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Tooltip, Button, Select as AntSelect } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTrash } from '@fortawesome/pro-solid-svg-icons/faTrash'
import { faAdd } from '@fortawesome/pro-solid-svg-icons/faAdd'
import { faBook } from '@fortawesome/pro-solid-svg-icons/faBook'
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons/faInfoCircle'
import { RankmiTable } from '@rankmi/rankmi-table'
import { IRelatedCourse, ISimpleCourse } from 'interfaces/learningPath'
import { Modal } from 'components/reusables/modal'
import Select from 'components/reusables/select'
import { ModalButtons } from 'components/reusables/modalButtons'
import EmptyState from 'components/reusables/emptyState'
import { useAppSelector, useAppDispatch } from 'store/hooks'
import { slices } from 'store/slices'
import { Options, AddCourse, Alert, AlertContent, Observations } from './styled'

const { Option } = AntSelect

interface IProps {
  collectionOrder: number
  relatedCourse: IRelatedCourse
  close: () => void
}

const AddPrerequisites = ({
  collectionOrder,
  relatedCourse,
  close,
}: IProps) => {
  const { t } = useTranslation()
  const prefix =
    'administration.learningPaths.manage.create.content.collections.courses.addPrerequisites'
  const dispatch = useAppDispatch()
  const [courses, setCourses] = useState<ISimpleCourse[]>([])
  const [selectedCourses, setSelectedCourses] = useState<number[]>([])
  const [mainCourses, setMainCourses] = useState('')
  const content = useAppSelector(slices.learningPath.content)

  useEffect(() => {
    getCourses()
    getMainCourses()
  }, [])

  const getCourses = () => {
    if (relatedCourse.prerequisites.length) {
      setSelectedCourses(relatedCourse.prerequisites.map((item) => item.id))
    } else {
      setSelectedCourses([0])
    }
    const newCourses: ISimpleCourse[] = []
    for (const collection of content.collections) {
      for (const newRelatedCourse of collection.relatedCourses) {
        if (newRelatedCourse.course.id !== relatedCourse.course.id) {
          newCourses.push(newRelatedCourse.course)
        }
      }
    }
    setCourses(newCourses)
  }

  const getMainCourses = () => {
    let result = ''
    content.collections.forEach((collection) => {
      collection.relatedCourses.forEach((newRelatedCourse) => {
        const prerequisites = newRelatedCourse.prerequisites.map(
          (prerequisite) => prerequisite.id
        )
        if (prerequisites.includes(relatedCourse.course.id)) {
          result = result.length
            ? `${result}, ${newRelatedCourse.course.name}`
            : newRelatedCourse.course.name
        }
      })
    })
    setMainCourses(result)
  }

  const onSave = () => {
    dispatch(
      slices.learningPath.updateCoursePrerequisites({
        selectedCourses: courses.filter((course) =>
          selectedCourses.includes(course.id)
        ),
        courseOrder: relatedCourse.order,
        collectionOrder: collectionOrder,
      })
    )
    close()
  }

  const onRemove = (id: number) => {
    const newSelectedCourses = selectedCourses.filter((item) => item !== id)
    setSelectedCourses(newSelectedCourses.length > 0 ? newSelectedCourses : [0])
  }

  const onChangeCourse = (previousCourse: number, course: number) => {
    setSelectedCourses(
      selectedCourses.map((item) => (item === previousCourse ? course : item))
    )
  }

  const locale = {
    header: {
      rowsCount: t(`${prefix}.rowsCount`),
      rowsCount_plural: t(`${prefix}.rowsCountPlural`),
    },
  }

  const columns = [
    {
      title: t(`${prefix}.columns.name.title`),
      render: (id: number) => (
        <Select
          showSearch={true}
          value={id || undefined}
          placeholder={t(`${prefix}.columns.name.placeholder`)}
          onSelect={(value) => onChangeCourse(id, value)}
          notFoundContent={
            <EmptyState
              title={t(`${prefix}.columns.name.emptyState`)}
              icon={faBook}
            />
          }
        >
          {courses
            .filter(
              (item) => !selectedCourses.includes(item.id) || item.id === id
            )
            .map((item) => (
              <Option key={item.id} value={item.id} label={item.name}>
                {item.name}
              </Option>
            ))}
        </Select>
      ),
    },
    {
      width: 50,
      render: (id: number) => (
        <Options>
          <Tooltip
            title={t(`${prefix}.columns.options.remove`)}
            placement="right"
          >
            <Button
              onClick={() => onRemove(id)}
              disabled={id === 0}
              type="link"
            >
              <FontAwesomeIcon icon={faTrash} />
            </Button>
          </Tooltip>
        </Options>
      ),
    },
  ]

  return (
    <Modal
      title={t(`${prefix}.title`)}
      actions={
        <ModalButtons
          primaryText={t(`${prefix}.buttons.primary`)}
          primaryAction={onSave}
          secondaryText={t(`${prefix}.buttons.secondary`)}
          secondaryAction={close}
        />
      }
      maxheight={400}
      maxwidth={800}
      close={close}
    >
      <>
        {mainCourses.length > 0 && (
          <Alert>
            <FontAwesomeIcon icon={faInfoCircle} />
            <AlertContent>
              {t(`${prefix}.alert`)}
              <span>{mainCourses}</span>.
            </AlertContent>
          </Alert>
        )}
        <Observations>
          {t(`${prefix}.observations`)}“{relatedCourse.course.name}”.{' '}
          <span>{t(`${prefix}.observationsSuffix`)}</span>
        </Observations>
        <RankmiTable
          locale={locale}
          columnHeaders={columns}
          data={selectedCourses}
          total={
            selectedCourses.includes(0)
              ? selectedCourses.length - 1
              : selectedCourses.length
          }
          countIcon={faBook}
        />
        <AddCourse>
          <Button
            type="link"
            onClick={() => setSelectedCourses([...selectedCourses, 0])}
            disabled={selectedCourses.includes(0)}
          >
            <FontAwesomeIcon icon={faAdd} />
            {t(`${prefix}.buttons.addCourse`)}
          </Button>
        </AddCourse>
      </>
    </Modal>
  )
}

export default AddPrerequisites
